import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import * as AuthServices from '../../services/auth.service';
import { ATLEAST_ONE_CAPITAL_REGEX, ATLEAST_ONE_NUMBER_REGEX, ATLEAST_ONE_SMALL_REGEX, ATLEAST_ONE_SPECIAL_CHARACTER_REGEX, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, SWAL_SETTINGS } from "../../../../utils/Constants";
import { PHONE_VALIDATION } from "../../../../utils/CommonValidatiobn";
import Swal from "sweetalert2";
import { handleServerValidations } from "../../../../utils/commonfunction";
import { useNavigate, useSearchParams } from "react-router-dom";
import PhoneInput from "../../../../utils/PhoneInput";
import GoogleLogin from "./googleLogin";
import AppleLoginButton from "./appleLogin";
import { useTranslation } from "react-i18next";
import { Camera } from "lucide-react";
import CameraCaptureModal from "../../components/CameraCaptureModal";
// Import social icons if you are using image assets
// import GoogleIcon from "/images/google-icon.svg";
// import AppleIcon from "/images/apple-icon.svg";

function SignupBuyer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirect") || "/";
  const { t } = useTranslation();

  const [loginMethod, _] = useState("email"); // State to manage the active login method (Email or Phone Number)
  const [showPassword, setShowPassword] = useState(false); // State to manage password visibility (for demonstration)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [__, setSubmitted] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);

  // Define primary colors matching the image
  // const primaryPink = "#FF63B5"; // A close match to the button/highlight color
  // const lightPink = "rgb(255, 230, 235)"; // A close match to the light pink background

  const validationSchema = Yup.object().shape({
    profile_image: Yup.mixed()
      .required(t("validation_error_profile_img_required") || "Profile picture is required"),
    name: Yup.string()
      .required(t("vaildation.name")),
    username: loginMethod === 'email'
      ? Yup.string()
        .trim()
        .required(t("vaildation.username_required"))
        .matches(/^[a-z0-9_.-]+$/, t("vaildation.username_invalid") || "Username can only contain lowercase letters, numbers, underscores, dots, and hyphens")
      : Yup.string().notRequired(),
    email: Yup.string()
      .trim()
      .email(t("vaildation.email_required"))
      .matches(
        /^[a-zA-Z0-9._%+-]+@[A-Za-z0-9-]+\.[A-Za-z]{2,63}$/,
        t("vaildation.valid_email_address")
      )
      .required(t("vaildation.email_required")),
    password: loginMethod === 'email'
      ? Yup.string()
        .required(t("vaildation.new_password"))
        .matches(ATLEAST_ONE_SMALL_REGEX, t("vaildation.one_small_letter"))
        .matches(ATLEAST_ONE_CAPITAL_REGEX, t("vaildation.one_capital_letter"))
        .matches(ATLEAST_ONE_NUMBER_REGEX, t("vaildation.one_number"))
        .matches(ATLEAST_ONE_SPECIAL_CHARACTER_REGEX, t("vaildation.one_special_char"))
        .min(PASSWORD_MIN_LENGTH, t("vaildation.min_length"))
        .max(PASSWORD_MAX_LENGTH, t("vaildation.max_length"))
      : Yup.string().notRequired(),
    confirm_password: loginMethod === 'email'
      ? Yup.string().required(t("vaildation.confirm_password")).oneOf([Yup.ref("password"), null], t("vaildation.password_not_match"))
      : Yup.string().notRequired(),
    phone_number: Yup.string()
      .required(t("vaildation.kyc_mobile_number_required"))
      .matches(/^\d{10}$/, t("vaildation.kyc_mobile_number_invalid")),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      profile_image: null,
      name: '',
      username: '',
      email: '',
      password: '',
      confirm_password: '',
      phone_number: ''
    },
    validationSchema: validationSchema,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting, resetForm, setFieldError }) => {
      setSubmitted(true);
      let formData = new FormData();

      if (values.profile_image) {
        formData.append("profile_image", values.profile_image);
      }
      formData.append("name", values.name);
      if (loginMethod === 'email') {
        formData.append("username", values.username);
        formData.append("email", values.email);
        formData.append("password", values.password);
        formData.append("confirm_password", values.confirm_password);
      }
      formData.append("phone_number", values.phone_number);
      formData.append("type", loginMethod);

      setSubmitted(false);
      AuthServices
        .signup(formData)
        .then((response) => {
          setSubmitted(true);

          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            navigate(`../otp?user_id=${response.data.user_id}&login_method=${loginMethod}`);
          } else {
            const errText = handleServerValidations(response);
            if (errText && (errText.toLowerCase().includes("profile") || errText.toLowerCase().includes("image"))) {
              setFieldError("profile_image", errText);
            }
            if (errText && (errText.toLowerCase().includes("phone") || errText.toLowerCase().includes("mobile"))) {
              setFieldError("phone_number", errText);
            }
            if (errText && errText.toLowerCase().includes("email")) {
              setFieldError("email", errText);
            }
            if (errText && errText.toLowerCase().includes("username")) {
              setFieldError("username", errText);
            }
            Swal.fire({
              icon: "error",
              text: errText,
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          setSubmitted(true);
          console.log("error ====> ", error);
        });
    },
  });

  console.log(formik.errors);

  const handleOpenProfileCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      Swal.fire({
        icon: "warning",
        text: "Please connect camera first",
        ...SWAL_SETTINGS,
      });
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((device) => device.kind === "videoinput");
      if (devices.length > 0 && videoInputs.length === 0) {
        Swal.fire({
          icon: "warning",
          text: "Please connect camera first",
          ...SWAL_SETTINGS,
        });
        return;
      }
      setIsCameraModalOpen(true);
    } catch (err) {
      console.error("Camera check error:", err);
      Swal.fire({
        icon: "warning",
        text: "Please connect camera first",
        ...SWAL_SETTINGS,
      });
    }
  };

  const handlePhotoCaptured = (file, previewUrl) => {
    formik.setFieldValue("profile_image", file);
    setPreviewImage(previewUrl);
    formik.setFieldError("profile_image", "");
  };

  return (
    <section className="py-8 md:py-12 lg:py-16 form-bg">
      <div className="container">
        <div className="mb-8">
          <h1 className="headings-h1 text-center text-zinc-950 mb-2">{t("signup")}</h1>
          <p className="text-center text-zinc-500 body-lg">
            {t("create_account")}
          </p>
        </div>
        <div className="bg-white p-[15px] md:p-[50px] rounded-[10px] max-w-[475px] shadow-[0px_2px_24px_rgba(0,0,0,0.05)] w-full mx-auto border border-zinc-100 w-full">
          <form onSubmit={formik.handleSubmit} className="space-y-5">
         
            {/* Profile Picture Camera Capture */}
            <div className="form-group flex flex-col items-center justify-center pb-2">
              <div className="relative group">
                <button
                  type="button"
                  onClick={handleOpenProfileCamera}
                  className="w-24 h-24 rounded-full overflow-hidden border-2 border-dashed border-zinc-300 hover:border-[#FF63B5] bg-zinc-50 flex items-center justify-center cursor-pointer transition-all duration-200 shadow-sm block relative focus:outline-none"
                  title="Take profile picture"
                >
                  {previewImage ? (
                    <img
                      src={previewImage}
                      alt="Profile preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-zinc-300">
                      <svg
                        className="w-12 h-12 text-zinc-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleOpenProfileCamera}
                  className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-[#FF63B5] text-white flex items-center justify-center cursor-pointer shadow-md hover:bg-[#e04f9f] transition-colors !mb-0 focus:outline-none"
                  title="Take profile picture"
                >
                  <Camera className="w-3.5 h-3.5" />
                </button>
              </div>
              <button
                type="button"
                onClick={handleOpenProfileCamera}
                className="mt-2 text-center cursor-pointer font-bold block bg-transparent border-0 p-0 text-zinc-900 hover:text-[#FF63B5] transition-colors focus:outline-none"
              >
                {t("label_profile_picture")} <span className="text-danger">*</span>
              </button>
              <span className="text-danger text-xs mt-1 text-center d-blockerr-spn">
                {formik.errors.profile_image && (formik.touched.profile_image || formik.submitCount > 0) && formik.errors.profile_image}
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="name">{t("name")} <span className="text-danger">*</span></label>
              <input
                id='name'
                type="text"
                className="form-control-custom"
                placeholder={t("placeholder.name")}
                // required
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <span className="text-danger text-left d-blockerr-spn">
                {formik.errors.name && formik.touched.name && formik.errors.name}
              </span>
            </div>

            {loginMethod === "email" && (
              <div className="form-group">
                <label htmlFor="username">Username <span className="text-danger">*</span></label>
                <input
                  id='username'
                  type="text"
                  className="form-control-custom"
                  placeholder="Enter username"
                  name="username"
                  value={formik.values.username}
                  onChange={(e) => {
                    const val = e.target.value.toLowerCase().replace(/\s/g, "");
                    formik.setFieldValue("username", val);
                  }}
                  onBlur={formik.handleBlur}
                />
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.username && formik.touched.username && formik.errors.username}
                </span>
              </div>
            )}



            {/* Password Field */}
            {
              loginMethod === "email"
                ?
                <>
                  {/* Input Fields */}
                  <div className="form-group">
                    <label htmlFor="email" className="">{t("email_address")} <span className="text-danger">*</span></label>
                    <input
                      id="email"
                      type="email"
                      placeholder={t("placeholder.email")}
                      className="form-control-custom"
                      // required
                      name="email"
                      value={formik.values.email}
                      onChange={(e) => {
                        const val = e.target.value.toLowerCase().replace(/\s/g, "");
                        formik.setFieldValue("email", val);
                      }}
                      onBlur={formik.handleBlur}
                    />
                    <span className="text-danger text-left d-blockerr-spn">
                      {formik.errors.email && formik.touched.email && formik.errors.email}
                    </span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone_number">{t("phone_number")} <span className="text-danger">*</span></label>
                    <div className="flex items-center form-control-custom p-0 overflow-hidden">
                      <span className="px-3 text-zinc-500 bg-zinc-100 h-full flex items-center border-r border-zinc-200 text-sm">
                        +91
                      </span>
                      <input
                        id="phone_number"
                        type="text"
                        placeholder={t("placeholder.phone")}
                        className="flex-1 px-3 outline-none bg-transparent h-full h-[44px]"
                        name="phone_number"
                        value={formik.values.phone_number}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                          formik.setFieldValue("phone_number", val);
                        }}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                    <span className="text-danger text-left d-blockerr-spn">
                      {formik.errors.phone_number && (formik.touched.phone_number || formik.submitCount > 0) && (t(formik.errors.phone_number) || formik.errors.phone_number)}
                    </span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="password" className="">
                      {t("password")} <span className="text-danger">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={t("placeholder.password")}
                        className="form-control-custom !pr-[40px]"
                        name="password"
                        value={formik.values.password}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\s/g, ""); // remove spaces
                          formik.setFieldValue("password", value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === " ") {
                            e.preventDefault(); // block space
                          }
                        }}
                        onBlur={formik.handleBlur}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(prev => !prev)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <img src="/images/eye2.svg" alt="Show" /> : <img src="/images/eye1.svg" alt="Hide" />}
                      </button>
                    </div>
                    <span className="text-danger text-left d-blockerr-spn">
                      {formik.errors.password && formik.touched.password && formik.errors.password}
                    </span>
                  </div>

                  {/* Confirm Password Field */}
                  <div className="form-group">
                    <label htmlFor="confirm_password" className="">
                      {t("confirm_password")} <span className="text-danger">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="confirm_password"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder={t("placeholder.confirm_password")}
                        className="form-control-custom !pr-[40px]"
                        name="confirm_password"
                        value={formik.values.confirm_password}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\s/g, "");
                          formik.setFieldValue("confirm_password", value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === " ") {
                            e.preventDefault();
                          }
                        }}
                        onBlur={formik.handleBlur}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <img src="/images/eye2.svg" alt="Show" /> : <img src="/images/eye1.svg" alt="Hide" />}
                      </button>
                    </div>
                    <span className="text-danger text-left d-blockerr-spn">
                      {formik.errors.confirm_password && formik.touched.confirm_password && formik.errors.confirm_password}
                    </span>
                  </div>
                </>
                :
                <div className="form-group">
                  <div className="flex items-center form-control-custom p-0 overflow-hidden">
                    <span className="px-3 text-zinc-500 bg-zinc-100 h-full flex items-center border-r border-zinc-200 text-sm">
                      +91
                    </span>
                    <input
                      id="phone_number_alt"
                      type="text"
                      placeholder={t("placeholder.phone")}
                      className="flex-1 px-3 outline-none bg-transparent h-full h-[44px]"
                      name="phone_number"
                      value={formik.values.phone_number}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                        formik.setFieldValue("phone_number", val);
                      }}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  <span className="text-danger text-left d-blockerr-spn">
                    {formik.errors.phone_number && formik.touched.phone_number && t(formik.errors.phone_number)}
                  </span>
                </div>
            }



            {/* Terms and Privacy Policy Checkbox */}
            <div className="flex items-start gap-2.5 mb-5 mt-3 text-left">
              <input
                id="terms-checkbox"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded cursor-pointer accent-[#FF63B5] flex-shrink-0"
              />
              <label htmlFor="terms-checkbox" className="text-xs md:text-sm text-zinc-600 cursor-pointer select-none leading-snug">
                I accept the{" "}
                <a
                  href="/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-500 hover:underline font-semibold"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms and Conditions
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-500 hover:underline font-semibold"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!termsAccepted}
              className={`btn btn-pri w-full transition-all duration-200 ${
                !termsAccepted ? "opacity-50 cursor-not-allowed !pointer-events-none" : ""
              }`}
            >
              {t("submit")}
            </button>

            {/* OR Divider */}
            <div className="relative text-center my-4">
              <span className="relative z-10 inline-block px-4 text-zinc-800 bg-white body-2 medium">
                {t("continue_with")}
              </span>
            </div>

            {/* Social Login Buttons */}
            <div className="flex space-x-3.5">
              {/* Google Icon Placeholder */}
              {/* <button
                type="button"
                className="flex-1 flex items-center justify-center"
              >
                <img src="/images/google.svg" alt="Google" />
              </button> */}
              <GoogleLogin />

              {/* Apple Icon Placeholder */}
              {/* <button
                type="button"
                className="flex-1 flex items-center justify-center"
              >
                <img src="/images/apple.svg" alt="Apple" />
              </button> */}
              <AppleLoginButton />

            </div>

            {/* Guest User Button */}
            {/* <button type="button" className="btn btn-outline w-full" onClick={() => navigate(redirectUrl)}>
              {t("guest_user") || "Continue as Guest"}
            </button> */}
          </form>
        </div>
        <div className="mt-8 text-center body-md text-zinc-700">
          {t("already_account")}
           <a
            href="#!"
            className="headings-h6 bold text-[var(--secondary-400)] ml-2"
            onClick={(e) => { e.preventDefault(); navigate('../login') }}
          >
            {t("login")}
          </a>
        </div>
      </div>
      <CameraCaptureModal
        isOpen={isCameraModalOpen}
        onClose={() => setIsCameraModalOpen(false)}
        onCapture={handlePhotoCaptured}
      />
    </section>
  );
}

export default SignupBuyer;
